import { useTheme } from '@mui/material/styles';
import type { RouteComponentProps } from '@reach/router';
import { subDays, compareAsc } from 'date-fns';
import dayjs from 'dayjs';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DrilldownHeader } from '@components/DrilldownHeader';
import DrilldownItem from '@components/DrilldownItem';
import {
  Description,
  Title,
} from '@components/DrilldownItem/DrilldownItem.style';
import { InlineMessage } from '@components/InlineMessage';
import RRuleNlp from '@components/RRuleNlp';
import RoutineList from '@components/RoutineList';
import YourNotes from '@components/YourNotes';

import { ROUTINES } from '@constants/routes';

import { useGetRoutine } from '@hooks/api/routine';
import { useApp } from '@hooks/useApp';
import useRoutines from '@hooks/useRoutines';

import AppLayout from '@layouts/AppLayout';
import {
  FlexColumn,
  FlexColumnContainer,
  MainContainer,
  PageBody,
} from '@layouts/DrilldownStyle';

import { RoutineRecord } from '@models/routine';

import { formatDate, capitalize, formatTime } from '@utils/format';

import CompletedItem from './TodaysRoutine';

const messages = defineMessages({
  startDate: {
    id: 'routineDrilldown.startDate',
    defaultMessage: 'Start Date',
  },
  endDate: {
    id: 'routineDrilldown.endDate',
    defaultMessage: 'End Date',
  },
  frequency: {
    id: 'routineDrilldown.frequency',
    defaultMessage: 'Frequency',
  },
  instructions: {
    id: 'routineDrilldown.instructions',
    defaultMessage: 'Instructions',
  },
  title: {
    id: 'routineDrilldown.title',
    defaultMessage: 'Routine',
  },
  repeats: {
    id: 'routineDrilldown.repeats',
    defaultMessage: 'Repeats',
  },
  startedOn: {
    id: 'routineDrilldown.startedOn',
    defaultMessage: 'Started on',
  },
  times: {
    id: 'routineDrilldown.times',
    defaultMessage: 'Times',
  },
  from: {
    id: 'routineDrilldown.from',
    defaultMessage: 'from',
  },
  to: {
    id: 'routineDrilldown.to',
    defaultMessage: 'to',
  },
  pastRoutines: {
    id: 'routineDrilldown.pastRoutines',
    defaultMessage: 'Past Routines',
  },
  todaysRoutines: {
    id: 'routineDrilldown.todaysRoutines',
    defaultMessage: "Today's Routines",
  },
  noRecords: {
    id: 'routineDrilldown.noRecords',
    defaultMessage: 'This routine does not happen today.',
  },
  notCompleted: {
    id: 'GLOBAL.notCompleted',
    defaultMessage: 'Not completed',
  },
  completed: {
    id: 'GLOBAL.completed',
    defaultMessage: 'Completed',
  },
});

const RoutineDetails: React.FC<RouteComponentProps & { id?: string }> = ({
  id = '',
}) => {
  const { formatMessage } = useIntl();

  const { palette } = useTheme();

  const { data: routine, loading } = useGetRoutine({ record_id: id });
  const routineEvent = React.useMemo(
    () => routine?.event ?? undefined,
    [routine?.event]
  );
  const routinesRange = React.useMemo(() => {
    const event = routine?.event;
    if (!event) {
      return [];
    }
    return RoutineRecord.getRoutinesRange(
      [event],
      subDays(dayjs(new Date()).startOf('day').toDate(), 8),
      subDays(dayjs(new Date()).endOf('day').toDate(), 1)
    );
  }, [routine]);
  const todaysRoutines = React.useMemo(() => {
    if (!routine) {
      return [];
    }
    return RoutineRecord.getTodaysRoutines([routine]);
  }, [routine]);
  const timeSlots = React.useMemo(() => {
    if (!routineEvent) {
      return [];
    }
    return RoutineRecord.getTimeSlots(routineEvent);
  }, [routineEvent]);

  const { selectRoutine } = useApp();
  const { isRoutineComplete } = useRoutines();

  useEffect(() => {
    if (!routine && !loading) {
      navigate(ROUTINES);
    }
  }, [loading, routine]);

  return (
    <AppLayout friendlyName={routine?.name} loading={loading}>
      <DrilldownHeader
        actions={[
          {
            title: 'edit',
            handleClick: () => {
              selectRoutine({
                routine: todaysRoutines.at(0) ?? routineEvent,
              });
              navigate(`${ROUTINES}`);
            },
          },
        ]}
        backRoute={ROUTINES}
        title={routine?.name ?? formatMessage(messages.title)}
      />
      <MainContainer maxWidth="1200px">
        <FlexColumnContainer>
          <FlexColumn>
            <PageBody disableTransparent>
              <Title name="todays-routines">
                {formatMessage(messages.todaysRoutines)}
              </Title>
              {todaysRoutines.length ? (
                todaysRoutines.map((todaysRoutine, index) => {
                  return (
                    <CompletedItem
                      key={`todays-routine-${index}`}
                      routine={todaysRoutine}
                      time={new Date(todaysRoutine.startDate)}
                    />
                  );
                })
              ) : (
                <div style={{ color: palette.text.secondary }}>
                  {formatMessage(messages.noRecords)}
                </div>
              )}
            </PageBody>

            {routineEvent ? (
              <PageBody>
                <DrilldownItem
                  label={formatMessage(messages.startedOn)}
                  type="date"
                  value={formatDate({ date: new Date(routineEvent.startDate) })}
                />
                <div>
                  <Title name={formatMessage(messages.times)}>
                    {formatMessage(messages.times)}
                  </Title>
                  <div>
                    {timeSlots.map((slot, index) => {
                      return (
                        <Description key={`timeslot-${index}`}>
                          {`${capitalize(
                            formatMessage(messages.from)
                          )} ${formatTime(slot.startTime)} ${formatMessage(
                            messages.to
                          )} ${formatTime(slot.endTime)}`}
                        </Description>
                      );
                    })}
                  </div>
                </div>
                <DrilldownItem
                  label={formatMessage(messages.instructions)}
                  value={routineEvent.instructions}
                />
                {routineEvent.rRule ? (
                  <div>
                    <Title name={formatMessage(messages.repeats)}>
                      {formatMessage(messages.repeats)}
                    </Title>
                    <RRuleNlp
                      rrule={routineEvent.rRule}
                      timeSlots={timeSlots}
                    />
                  </div>
                ) : null}
              </PageBody>
            ) : null}

            <RoutineList
              listItems={routinesRange
                .sort((routineA, routineB) =>
                  compareAsc(
                    new Date(routineB.startDate),
                    new Date(routineA.startDate)
                  )
                )
                .map((routine) => {
                  const isCompleted = isRoutineComplete(routine);
                  return {
                    subtitle: dayjs(new Date(routine.startDate)).format(
                      'MMMM DD, YYYY h:mm A'
                    ),
                    child: (
                      <InlineMessage
                        message={formatMessage(
                          isCompleted
                            ? messages.completed
                            : messages.notCompleted
                        )}
                        type={isCompleted ? 'success' : 'error'}
                      />
                    ),
                  };
                })}
              title={formatMessage(messages.pastRoutines)}
              titleColor={palette.primary.contrastText}
            />
          </FlexColumn>

          <YourNotes notes={routine?.recordNotes} />
        </FlexColumnContainer>
      </MainContainer>
    </AppLayout>
  );
};

export default RoutineDetails;
